import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { api, formatApiError, formatDate } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { AdsPanel } from "@/components/admin/AdsPanel";

const today = new Date().toISOString().slice(0, 10);
const inMonth = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);
const EMPTY = { name: "", code: "", from_package: "ANY", to_package: "GOLD", discount_percent: "", start_date: today, end_date: inMonth };

export default function CampaignsPage() {
  const [items, setItems] = useState([]);
  const [packages, setPackages] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    const [{ data }, { data: p }] = await Promise.all([api.get("/campaigns"), api.get("/packages")]);
    setItems(data);
    setPackages(p.packages);
  };
  useEffect(() => { load(); }, []);

  const name = (code) => packages.find((p) => p.code === code)?.name || "Herhangi";

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/campaigns", { ...form, from_package: form.from_package === "ANY" ? null : form.from_package, discount_percent: Number(form.discount_percent) });
      toast.success("Kampanya oluşturuldu");
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="campaigns-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Süper Admin</div>
          <h1 className="text-4xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Kampanyalar</h1>
          <p className="text-slate-500 mt-1">Paketler arası geçişlerde uygulanacak indirimler. İndirim, geçişten sonraki ilk ödemeye uygulanır.</p>
        </div>
        <Button data-testid="add-campaign-btn" onClick={() => setOpen(true)} className="bg-slate-900"><Plus className="w-4 h-4 mr-2" />Yeni Kampanya</Button>
      </div>

      <Card className="divide-y">
        {items.length === 0 && <div className="p-10 text-center text-slate-400">Henüz kampanya yok.</div>}
        {items.map((c) => (
          <div key={c.id} data-testid={`campaign-row-${c.code}`} className="p-4 flex items-center justify-between gap-4">
            <div>
              <div className="font-semibold">{c.name} <span className="font-mono text-xs bg-slate-100 px-2 py-0.5 rounded ml-1">{c.code}</span></div>
              <div className="text-xs text-slate-500 mt-1">
                {name(c.from_package)} → {name(c.to_package)} · {formatDate(c.start_date)} – {formatDate(c.end_date)}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Badge className="bg-emerald-600">%{c.discount_percent}</Badge>
              <Switch checked={c.active} onCheckedChange={async () => { await api.put(`/campaigns/${c.id}/toggle`); load(); }} />
              <Button size="sm" variant="ghost" onClick={async () => { await api.delete(`/campaigns/${c.id}`); load(); }}><Trash2 className="w-4 h-4 text-red-500" /></Button>
            </div>
          </div>
        ))}
      </Card>

      <AdsPanel />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Yeni Kampanya</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Kampanya Adı *</Label><Input data-testid="campaign-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label>Kod *</Label><Input data-testid="campaign-code" required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="GOLD20" /></div>
              <div>
                <Label>Mevcut Paket</Label>
                <Select value={form.from_package} onValueChange={(v) => setForm({ ...form, from_package: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ANY">Herhangi</SelectItem>
                    {packages.map((p) => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Hedef Paket</Label>
                <Select value={form.to_package} onValueChange={(v) => setForm({ ...form, to_package: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{packages.map((p) => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>İndirim (%) *</Label><Input data-testid="campaign-discount" type="number" min="1" max="100" required value={form.discount_percent} onChange={(e) => setForm({ ...form, discount_percent: e.target.value })} /></div>
              <div />
              <div><Label>Başlangıç</Label><Input type="date" required value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
              <div><Label>Bitiş</Label><Input type="date" required value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>İptal</Button>
              <Button data-testid="campaign-save-btn" type="submit" className="bg-emerald-600 hover:bg-emerald-700">Kaydet</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
